import React, { FC, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, message } from "antd";
import { HomeSeriesContent } from "../home/HomeSeriesContent";
import { useQuickSeriesSurface } from "../home/useQuickSeriesSurface";
import { useSeriesButtonPermissions } from "../../hooks/useSeriesButtonPermissions";
import { useAlert } from "../../hooks/useAlert";
import { AlertMessage, AutoModel } from "../../components/common";
import { deleteFastTranslateSeriesApi, renameFastTranslateSeriesApi } from "../../service/fastTranslateSeries";
import type { HomeSeriesFolderItem } from "../home/types";
import {
  OUTER_SERIES_LIST_PAGE_PARAM,
  PREMIUM_TRANSLATION_FULL_PATH,
  QUICK_SERIES_INNER_BASE,
} from "../../utils/menuPathNormalize";
import { usePremiumWorkspaceListMeta } from "../home/premiumWorkspaceContext";
import { useUserRole } from "../../hooks/useUserRole";
import { QuickTransCreateSeriesModal } from "./QuickTransCreateSeriesModal";
import "../home/homeModals.scss";

/**
 * 一键速译外层：与精品译制共用 PremiumWorkspaceLayout + HomeSeriesContent；新建剧集走上传弹窗。
 */
export const QuickTranslateHome: FC = () => {
  const navigate = useNavigate();
  const workspaceListMeta = usePremiumWorkspaceListMeta();
  const surface = useQuickSeriesSurface();
  const { hasCreateSeriesPermission, hasSeriesDeletePermission } = useSeriesButtonPermissions();
  const { isOperator, isAdmin, isAssessor, isLoading: roleLoading } = useUserRole();
  const { alertConfig, hideAlert, showSuccess, showError, showWarning } = useAlert();

  const [renameModal, setRenameModal] = useState<{
    visible: boolean;
    item: HomeSeriesFolderItem | null;
    newName: string;
  }>({ visible: false, item: null, newName: "" });

  const [createModalOpen, setCreateModalOpen] = useState(false);

  useEffect(() => {
    workspaceListMeta.setListTotalCount(surface.totalCount);
  }, [surface.totalCount, workspaceListMeta]);

  useEffect(() => {
    if (roleLoading) return;
    if (isOperator && !isAdmin && !isAssessor) {
      navigate(PREMIUM_TRANSLATION_FULL_PATH, { replace: true });
    }
  }, [roleLoading, isOperator, isAdmin, isAssessor, navigate]);

  const onFolderOpen = useCallback(
    (item: HomeSeriesFolderItem) => {
      if (!item.id) {
        message.warning("剧集ID不存在，无法打开剧集详情");
        return;
      }
      const q = new URLSearchParams();
      q.set("seriesId", String(item.id));
      q.set("seriesName", item.name || "");
      if (surface.page > 1) {
        q.set(OUTER_SERIES_LIST_PAGE_PARAM, String(surface.page));
      }
      navigate(`${QUICK_SERIES_INNER_BASE}?${q.toString()}`);
    },
    [navigate, surface.page]
  );

  const onNewSeries = useCallback(() => {
    if (!hasCreateSeriesPermission()) {
      showError("暂无创建剧集权限");
      return;
    }
    setCreateModalOpen(true);
  }, [hasCreateSeriesPermission, showError]);

  const onRename = useCallback((item: HomeSeriesFolderItem) => {
    setRenameModal({
      visible: true,
      item,
      newName: item.name || "",
    });
  }, []);

  const onDelete = useCallback(
    async (item: HomeSeriesFolderItem) => {
      if (!hasSeriesDeletePermission()) {
        showError("暂无删除权限");
        return;
      }
      try {
        const res = await deleteFastTranslateSeriesApi({ id: item.id });
        if (res?.code === 0 && res?.data) {
          showSuccess(`剧集「${item.name}」删除成功`);
          await surface.refresh();
          return;
        }
        showError(res?.msg || "删除失败");
      } catch (e: any) {
        showError(e?.msg || e?.message || "删除失败");
      }
    },
    [hasSeriesDeletePermission, showError, showSuccess, surface]
  );

  const handleRenameOk = useCallback(async () => {
    if (!renameModal.item || !renameModal.newName.trim()) {
      return;
    }
    const originalName = renameModal.item.name || "";
    const trimmed = renameModal.newName.trim();
    if (originalName === trimmed) {
      setRenameModal({ visible: false, item: null, newName: "" });
      return;
    }
    try {
      const res = await renameFastTranslateSeriesApi({
        seriesId: renameModal.item.id,
        name: trimmed,
      });
      if (res.code === 0 && res.data) {
        showSuccess("重命名成功");
        setRenameModal({ visible: false, item: null, newName: "" });
        await surface.refresh();
      } else {
        showError(res.msg || "重命名失败，请稍后重试");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "重命名失败，请稍后重试";
      showError(msg);
    }
  }, [renameModal.item, renameModal.newName, showError, showSuccess, surface]);

  return (
    <>
      <AlertMessage config={alertConfig} onClose={hideAlert} />
      <HomeSeriesContent
        embeddedInWorkspace
        totalCount={surface.totalCount}
        pageItems={surface.pageItems}
        page={surface.page}
        pageCount={surface.pageCount}
        hasPrev={surface.hasPrev}
        hasNext={surface.hasNext}
        isLoading={surface.isLoading}
        searchInputValue={surface.searchInputValue}
        onSearchChange={surface.handleSearchChange}
        timeSort={surface.timeSort}
        onTimeSortChange={surface.setTimeSort}
        scope={surface.scope}
        onScopeChange={surface.setScope}
        showScopeFilter={surface.showScopeFilter}
        onPagePrev={surface.goPrev}
        onPageNext={surface.goNext}
        onFolderOpen={onFolderOpen}
        onNewSeries={onNewSeries}
        onRename={onRename}
        onDelete={onDelete}
        canCreateSeries={hasCreateSeriesPermission()}
        canDeleteSeries={hasSeriesDeletePermission()}
      />

      <AutoModel
        open={renameModal.visible}
        onClose={() => setRenameModal({ visible: false, item: null, newName: "" })}
        title="重命名剧集"
        contentClassName="home-series-modal"
        footerRight={<>
          <Button onClick={() => setRenameModal({ visible: false, item: null, newName: "" })}>取消</Button>
          <Button type="primary" onClick={() => void handleRenameOk()}>确定</Button>
        </>}
      >
        <Input
          value={renameModal.newName}
          onChange={(e) => setRenameModal((prev) => ({ ...prev, newName: e.target.value }))}
          placeholder="请输入剧集名称"
          autoFocus
        />
      </AutoModel>

      <QuickTransCreateSeriesModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={() => void surface.refresh()}
        showError={showError}
        showSuccess={showSuccess}
        showWarning={showWarning}
      />
    </>
  );
};
